import type { SettingsView } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { Badge, Button, Card, ErrorState, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { shortDate } from "../theme";

/** The principal walkthrough. Step ids are stable: handlers in other streams may mark them too. */
export const GUIDE_STEPS: Array<{ id: string; to: string | null }> = [
  { id: "setup", to: "/setup" },
  { id: "import", to: "/import" },
  { id: "customer", to: "/customers" },
  { id: "faraday", to: null },
  { id: "message", to: "/inbox" },
  { id: "answer", to: "/forms" },
  { id: "pdf", to: "/forms" },
  { id: "carrier", to: "/carriers" },
  { id: "carrierImport", to: "/carriers" },
  { id: "review", to: "/customers" },
  { id: "persist", to: null },
];

const EXPLORE: Array<{ to: string; key: string }> = [
  { to: "/", key: "nav.today" }, { to: "/pipelines/sales", key: "nav.pipelines" }, { to: "/customers", key: "nav.customers" }, { to: "/inbox", key: "nav.inbox" },
  { to: "/renewals", key: "nav.renewals" }, { to: "/policies", key: "nav.policies" }, { to: "/forms", key: "nav.forms" }, { to: "/carriers", key: "nav.carriers" },
  { to: "/import", key: "nav.import" }, { to: "/documents", key: "nav.documents" }, { to: "/reports", key: "nav.reports" }, { to: "/settings", key: "nav.settings" },
];

export function GuideScreen() {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings"], queryFn: async () => (await callAction<"agency.settings.get", SettingsView>("agency.settings.get", {})).result });
  const mark = useMutation({
    mutationFn: (v: { step: string; done: boolean }) => callAction("agency.guide.mark", v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const guide = q.data.settings.guide;
  const doneCount = GUIDE_STEPS.filter((s) => guide[s.id]).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("guide.title")}</h1>
          <p className="text-ink/60">{t("guide.subtitle")}</p>
        </div>
        <Badge tone={doneCount === GUIDE_STEPS.length ? "green" : "neutral"}>{t("guide.progress").replace("{done}", String(doneCount)).replace("{total}", String(GUIDE_STEPS.length))}</Badge>
      </div>
      <div className="h-2 rounded-full bg-ink/10 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${(doneCount / GUIDE_STEPS.length) * 100}%` }} /></div>

      <Card>
        <ol className="divide-y divide-ink/5">
          {GUIDE_STEPS.map((s, i) => {
            const doneAt = guide[s.id];
            return (
              <li key={s.id} className={cn("flex gap-3 px-4 py-3", doneAt && "bg-primary/5")}>
                <button aria-label={doneAt ? t("guide.undo") : t("guide.markDone")} onClick={() => mark.mutate({ step: s.id, done: !doneAt })} className="mt-0.5 shrink-0 text-primary-dark">
                  {doneAt ? <CheckCircle2 className="h-6 w-6" /> : <Circle className="h-6 w-6 text-ink/30" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={cn("font-semibold", doneAt && "line-through text-ink/60")}>{i + 1}. {t(`guide.step.${s.id}`)}</p>
                  <p className="text-sm text-ink/60">{t(`guide.step.${s.id}.sub`)}</p>
                  {!s.to ? <p className="mt-1 inline-flex items-center gap-1 text-xs text-amber-800"><Lightbulb className="h-3.5 w-3.5" />{t("guide.tip")}: {t(`guide.step.${s.id}.sub`)}</p> : null}
                  {doneAt ? <p className="text-xs text-ink/50 mt-1">{t("guide.doneAt")} · {shortDate(doneAt, locale)}</p> : null}
                </div>
                <div className="flex shrink-0 flex-col gap-1 sm:flex-row sm:items-start">
                  {s.to ? <Link to={s.to} className="inline-flex min-h-[36px] items-center rounded-xl border border-ink/15 bg-white px-3 text-sm font-semibold hover:bg-ink/5">{t("guide.open")} →</Link> : null}
                  <Button variant={doneAt ? "ghost" : "outline"} size="sm" onClick={() => mark.mutate({ step: s.id, done: !doneAt })} loading={mark.isPending && mark.variables?.step === s.id}>{doneAt ? t("guide.undo") : t("guide.markDone")}</Button>
                </div>
              </li>
            );
          })}
        </ol>
      </Card>

      <section>
        <h2 className="text-lg font-bold">{t("guide.explore")}</h2>
        <p className="text-sm text-ink/60 mb-2">{t("guide.exploreSub")}</p>
        <ul className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {EXPLORE.map((e) => (
            <li key={e.to}><Link to={e.to} className="flex min-h-[48px] items-center rounded-2xl border border-ink/10 bg-white px-4 font-semibold hover:border-primary">{t(e.key)}</Link></li>
          ))}
        </ul>
      </section>
    </div>
  );
}
